import { DestroyRef, Injectable, NgZone, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { fromEvent } from 'rxjs';
import { GlobalSearchOverlayComponent } from './global-search-overlay.component';

/**
 * Opens the global search overlay (EB-3621 Plan 5.3) and owns the Ctrl/Cmd+K shortcut.
 * Single source for the header search bar and the keyboard shortcut, so at most one overlay
 * is ever open.
 */
@Injectable({ providedIn: 'root' })
export class GlobalSearchService {
  private readonly dialog = inject(MatDialog);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private ref: MatDialogRef<GlobalSearchOverlayComponent> | null = null;
  private initialised = false;

  /** Registers the global Ctrl/Cmd+K shortcut. Idempotent — a second call is a no-op so the
   * listener can never double-register (which would open-then-immediately-close the overlay).
   * The subscription is torn down with the service's injector (via takeUntilDestroyed), so it
   * doesn't outlive its instance — notably it no longer leaks across TestBed instances. */
  init(): void {
    if (this.initialised) return;
    this.initialised = true;
    fromEvent<KeyboardEvent>(document, 'keydown')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if ((event.ctrlKey || event.metaKey) && (event.key === 'k' || event.key === 'K')) {
          event.preventDefault();
          this.zone.run(() => this.toggle());
        }
      });
  }

  open(): void {
    if (this.ref) return;
    // Top-anchored and wide: it grows downward as results come in (content scrolls within
    // maxHeight), and the extra width lets quicklinks sit beside the findings.
    this.ref = this.dialog.open(GlobalSearchOverlayComponent, {
      width: '94vw',
      maxWidth: '1240px',
      maxHeight: '92vh',
      position: { top: '4vh' },
      panelClass: 'global-search-overlay-panel',
      autoFocus: 'input',
      restoreFocus: true,
    });
    this.ref.afterClosed().subscribe(() => { this.ref = null; });
  }

  close(): void {
    this.ref?.close();
  }

  toggle(): void {
    if (this.ref) this.close(); else this.open();
  }
}
